import React, { useEffect, useRef, useState } from 'react';
import { fetchFromPublicAPI } from '../services/common.js';
import { setInvoiceSaveOnPay } from '../services/paymentsService.js';

/**
 * VirtualTerminalForm
 * ===================
 *
 * Staff-facing "virtual terminal": lets the office key a card manually
 * and take a ONE-TIME payment against a specific invoice WITHOUT vaulting
 * the card. This is the manual-entry counterpart to pushing the invoice
 * to a physical terminal.
 *
 * How it works
 *   - Mounts the Rainforest Payment Component bound to the invoice's
 *     existing ``payin_config_id`` (the same config the public invoice
 *     page charges against). With the built-in pay button visible, the
 *     element performs the actual payin when staff submits.
 *   - Forces ``saveOnPayPreference = false`` on the invoice first so the
 *     payin webhook does NOT vault the card — this is an explicit
 *     one-time charge. The card details live only inside Rainforest's
 *     secure iframe; we never see the PAN/CVV and nothing is stored.
 *   - On the ``approved`` event the payin is submitted; settlement is
 *     finalised asynchronously by the payin webhook, so the parent shows
 *     its existing "waiting for payment" modal (card-on-file style).
 *
 * Props
 *   invoice  — the invoice being charged. Must carry ``publicHash`` and a
 *              payin config (``payinConfigData.payin_config_id`` or
 *              ``payinConfigId``).
 *   onPaid() — fires after Rainforest reports the payin approved.
 *   onCancel() — fires when staff dismisses the form.
 */
export default function VirtualTerminalForm({ invoice, onPaid, onCancel }) {
    const [sessionKey, setSessionKey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const componentRef = useRef(null);

    const hash = invoice?.publicHash;
    // The public invoice GET flattens this to ``payinConfigId``; the raw
    // staff invoice doc only carries the nested ``payinConfigData``. Accept
    // either so this works no matter which shape the parent passes.
    const payinConfigId =
        invoice?.payinConfigId || invoice?.payinConfigData?.payin_config_id || null;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);

            if (!hash) {
                setError('This invoice has no public link, so it cannot be charged here.');
                setLoading(false);
                return;
            }
            if (!payinConfigId) {
                setError('This invoice has no payment configuration. It may not have a merchant set up.');
                setLoading(false);
                return;
            }

            // Force the card NOT to be saved — a virtual-terminal charge is
            // explicitly one-time. Best-effort: if this fails we still want
            // the staff to be able to take the payment, but log it.
            try {
                await setInvoiceSaveOnPay(hash, false);
            } catch (err) {
                console.warn('[VirtualTerminal] could not clear save-on-pay preference', err);
            }

            let data;
            try {
                data = await fetchFromPublicAPI(`/api/payments/get-invoice-session/${hash}`);
            } catch (err) {
                if (cancelled) return;
                setError('Could not load the secure card form. Please try again.');
                setLoading(false);
                return;
            }
            if (cancelled) return;
            if (!data?.success || !data?.session_key) {
                setError(data?.error || 'Could not load the secure card form.');
                setLoading(false);
                return;
            }
            setSessionKey(data.session_key);
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [hash, payinConfigId]);

    useEffect(() => {
        if (!sessionKey || !componentRef.current) return undefined;
        const el = componentRef.current;

        const handleApproved = () => {
            onPaid?.();
        };
        const handleDeclined = () => {
            setError('The card was declined. Please try a different card.');
        };
        const handleError = () => {
            setError('There was a problem processing the card. Please try again.');
        };

        el.addEventListener('approved', handleApproved);
        el.addEventListener('declined', handleDeclined);
        el.addEventListener('error', handleError);

        return () => {
            el.removeEventListener('approved', handleApproved);
            el.removeEventListener('declined', handleDeclined);
            el.removeEventListener('error', handleError);
        };
    }, [sessionKey, onPaid]);

    return (
        <div style={{
            backgroundColor: '#fff',
            border: '1px solid #e3e7e4',
            borderRadius: 8,
            padding: 16,
            marginBottom: 20,
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 12,
            }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>Virtual Terminal — enter card to charge</div>
                <button
                    type="button"
                    onClick={onCancel}
                    style={{
                        padding: '4px 10px',
                        backgroundColor: '#fff',
                        color: '#6b716d',
                        border: '1px solid #cdd3cf',
                        borderRadius: 6,
                        fontSize: 12,
                        cursor: 'pointer',
                    }}
                >
                    Back
                </button>
            </div>

            <div style={{
                fontSize: 12,
                color: '#6b716d',
                marginBottom: 10,
                lineHeight: 1.4,
            }}>
                One-time payment. Card details are entered directly into NewtonPay's
                secure form — we never see the card number or CVV, and the card is
                <strong> not saved</strong>.
            </div>

            {loading && (
                <div style={{ color: '#6b716d', fontSize: 13 }}>Loading secure card form…</div>
            )}

            {!loading && sessionKey && payinConfigId && (
                // Keyed on sessionKey so a fresh session always remounts a
                // clean iframe instead of React reusing one with stale state.
                <rainforest-payment
                    key={sessionKey}
                    ref={componentRef}
                    session-key={sessionKey}
                    payin-config-id={payinConfigId}
                    allowed-methods="CARD"
                ></rainforest-payment>
            )}

            {error && (
                <div style={{ color: '#b42318', fontSize: 13, marginTop: 8 }}>{error}</div>
            )}
        </div>
    );
}
